import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { askToNotify } from '../lib/osNotify';
import {
  KINDS, KIND_NAMES, supported, canPickOutput, listDevices, nameOf, onDeviceChange,
  remembered, remember, resolve, isGone, audioConstraints, videoConstraints
} from '../lib/devices';
import './CallButton.css';

/**
 * The three selects: Microphone, Speakers, Camera.
 *
 * Gordon, 22 October: "I want the user to be able to select devices on his
 * computer for webcam, audio and microphone pls – some users have multiple
 * systems. I want them to be able to choose."
 *
 * ONE PANEL, TWO PLACES. It sits at the bottom of the Voice-or-Video menu, so
 * the choice is made before anybody is rung; and SessionPage opens the same
 * panel from the call footer, so a wrong choice can be put right without
 * hanging up. Both hand it the current choice and a function to call with a
 * new one; what happens next - remembering it, or swapping a live track - is
 * the caller's business. The rules about the browser live in lib/devices.js.
 *
 * TWO THINGS ARE SHOWN LIVE, because a list of names is not enough to tell a
 * headset from a speakerphone when both are called "USB Audio Device":
 *
 *   A MICROPHONE METER. A short test stream on the chosen microphone, read by
 *   an analyser and drawn as a bar. Tap the headset and the bar jumps: that
 *   is the one. In a call the page hands over the live microphone instead,
 *   so there is never a second capture of the same device.
 *
 *   A CAMERA PREVIEW, once a camera has been changed. Not before: a camera
 *   coming on unasked, inside a menu for a voice call, is exactly the thing
 *   that makes somebody distrust a call button.
 *
 * Both stop the moment the panel goes. A test stream left running keeps the
 * browser's recording indicator on, and that is a privacy complaint.
 *
 * Opening the test stream is also what UNLOCKS THE NAMES: enumerateDevices
 * gives blank labels until a permission has been granted, so the list is read
 * again as soon as the stream is up.
 */
export const DevicePanel = ({ choice, onChoose, meterStream = null, active = {},
  note = null, idPrefix = 'call-dev' }) => {
  const [list, setList] = useState(null);
  // Said in the panel, about the panel's own test stream; the caller's note
  // (a device the CALL could not open) arrives as a prop and is shown too.
  const [ownNote, setOwnNote] = useState(null);
  // The camera preview is only started once a camera has been CHANGED here.
  const [preview, setPreview] = useState(false);
  const meterRef = useRef(null);
  const previewRef = useRef(null);
  // The microphone the test stream is on, so a change can be told apart from
  // a re-render. Null until the panel has opened one of its own.
  const testStream = useRef(null);

  const refresh = useCallback(async () => { setList(await listDevices()); }, []);

  /** The list, now and whenever something is plugged in or pulled out. */
  useEffect(() => {
    refresh();
    return onDeviceChange(refresh);
  }, [refresh]);

  const micChoice = resolve('audioinput', list, choice);
  const outChoice = resolve('audiooutput', list, choice);
  const camChoice = resolve('videoinput', list, choice);

  /*
   * THE METER. Reads whichever stream it is given - the call's own
   * microphone when there is one, a short test stream on the chosen
   * microphone when there is not - and paints its level straight onto the
   * bar, outside React: sixty state changes a second to move a bar is a
   * render storm for nothing.
   */
  useEffect(() => {
    let stopped = false;
    let own = null;
    let ctx = null;
    let frame = 0;
    const bar = meterRef.current;

    (async () => {
      let stream = meterStream;
      if (!stream) {
        if (!navigator.mediaDevices?.getUserMedia) return;
        try {
          own = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints(micChoice.deviceId), video: false });
        } catch (err) {
          if (stopped) return;
          // The remembered microphone has gone: say so, and listen on the
          // default so the meter still means something.
          if (isGone(err) && micChoice.deviceId) {
            const was = choice?.audioinput?.label;
            setOwnNote(`${was ? `"${was}"` : 'The remembered microphone'} is not plugged in — `
              + 'using the system default.');
            try {
              own = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints(null), video: false });
            } catch { return; }
          } else {
            // Refused, or no microphone: the selects still work, the names
            // will not appear, and the hint under the list says why.
            return;
          }
        }
        if (stopped) { own.getTracks().forEach((t) => t.stop()); return; }
        testStream.current = own;
        stream = own;
        // The permission just granted is what fills in the names.
        refresh();
      }
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor || !bar) return;
      try {
        ctx = new Ctor();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        const tick = () => {
          if (stopped) return;
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i += 1) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          // RMS, lifted so quiet speech is visible: a raw RMS of 0.05 is a
          // normal voice and would be a bar 5% wide.
          const level = Math.min(1, Math.sqrt(sum / data.length) * 4);
          bar.style.width = `${Math.round(level * 100)}%`;
          bar.parentElement?.setAttribute('data-level', String(Math.round(level * 100)));
          frame = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        // No Web Audio, or a stream the browser will not analyse: the meter
        // stays flat, and nothing else is affected.
      }
    })();

    return () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      try { ctx?.close(); } catch { /* already closed */ }
      try { own?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
      if (testStream.current === own) testStream.current = null;
      if (bar) bar.style.width = '0%';
    };
    // The label is only for the note; the deviceId is what opens a stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterStream, micChoice.deviceId, refresh]);

  /** The preview, once a camera has been changed, on whichever is chosen. */
  useEffect(() => {
    if (!preview) return undefined;
    let stopped = false;
    let stream = null;
    const el = previewRef.current;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) return;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints(camChoice.deviceId), audio: false });
      } catch (err) {
        if (stopped) return;
        if (isGone(err) && camChoice.deviceId) {
          const was = choice?.videoinput?.label;
          setOwnNote(`${was ? `"${was}"` : 'The remembered camera'} is not plugged in — `
            + 'using the system default.');
        }
        setPreview(false);
        return;
      }
      if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
      if (el) el.srcObject = stream;
    })();
    return () => {
      stopped = true;
      try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
      if (el) el.srcObject = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, camChoice.deviceId]);

  const pick = (kind) => (e) => {
    const id = e.target.value;
    const device = id ? (list?.[kind] || []).find((d) => d.deviceId === id) : null;
    setOwnNote(null);
    onChoose(kind, device ? { deviceId: id, label: device.label } : null);
    if (kind === 'videoinput') setPreview(true);
  };

  const rows = (kind) => list?.[kind] || [];
  const gone = [micChoice, outChoice, camChoice].map((c) => c.missing).filter(Boolean);
  const nameFor = (kind, id) => {
    const i = rows(kind).findIndex((d) => d.deviceId === id);
    return i >= 0 ? nameOf(rows(kind)[i], i, kind) : null;
  };
  // "Now: Jabra Speak 510" under a select, when the caller knows what is
  // actually open - the track's own label, which is the truth whatever the
  // select says.
  const nowLine = (kind) => {
    // The track's label is Chrome's own, "Default - X"; shown the way the
    // list shows it.
    const name = (active?.[kind] && nameOf({ label: active[kind] }, 0, kind)) || (kind === 'audiooutput' && canPickOutput()
      ? (nameFor(kind, outChoice.deviceId) || (list ? 'System default' : null)) : null);
    return name ? <small className="call-dev-now">Now: {name}</small> : null;
  };

  if (!supported()) {
    return (
      <div className="call-dev" data-unsupported>
        <p className="call-dev-note">This browser does not let a page choose its
          microphone, speakers or camera. Pick them in your system's sound settings.</p>
      </div>
    );
  }

  return (
    <div className="call-dev">
      {KINDS.map((kind) => {
        const id = `${idPrefix}-${kind}`;
        const chosen = { audioinput: micChoice, audiooutput: outChoice, videoinput: camChoice }[kind];
        const output = kind === 'audiooutput';
        const cannot = output && !canPickOutput();
        return (
          <div className="call-dev-row" key={kind} data-kind={kind}>
            <label className="call-dev-label" htmlFor={id}>{KIND_NAMES[kind]}</label>
            {cannot ? (
              <select id={id} className="call-dev-select" disabled
                title="This browser does not let a page choose where sound comes out">
                <option>Chosen by the system</option>
              </select>
            ) : (
              <select id={id} className="call-dev-select"
                value={chosen.deviceId || ''} onChange={pick(kind)}>
                <option value="">System default</option>
                {rows(kind).map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>{nameOf(d, i, kind)}</option>
                ))}
              </select>
            )}
            {kind === 'audioinput' && (
              <div className="call-dev-meter" role="img" aria-label="Microphone level" data-level="0">
                <span className="call-dev-meter-bar" ref={meterRef} />
              </div>
            )}
            {cannot && (
              <small className="call-dev-now">
                Safari and Firefox play calls through the system's chosen speakers.
              </small>
            )}
            {!cannot && nowLine(kind)}
            {kind === 'videoinput' && preview && (
              <video ref={previewRef} className="call-dev-preview" autoPlay playsInline muted
                aria-label="Camera preview" />
            )}
          </div>
        );
      })}
      {list && !list.labelled && (
        <p className="call-dev-note" role="status">
          Device names appear once your microphone has been allowed.
        </p>
      )}
      {gone.map((g) => (
        <p className="call-dev-note call-dev-gone" role="status" key={g.id}>
          {g.label ? `"${g.label}"` : 'A remembered device'} is not plugged in — using the
          system default until it is.
        </p>
      ))}
      {ownNote && <p className="call-dev-note call-dev-gone" role="status">{ownNote}</p>}
      {/* The caller's note, unless the list above has already said the same
          thing about the same device. */}
      {note && !gone.some((g) => g.label && note.includes(`"${g.label}"`)) && (
        <p className="call-dev-note call-dev-gone" role="status">{note}</p>
      )}
    </div>
  );
};

/**
 * Start a call, with or without video.
 *
 * Gordon: "on the messenger i also want the call button next to the send
 * button with video call optionality once the call is pressed (ensure this
 * works everywhere)."
 *
 * So pressing it asks which kind, rather than guessing. Two reasons that is
 * the right shape rather than one button and a camera toggle afterwards:
 * turning a camera on inside a call that has already started means the other
 * person sees you before you have decided to be seen, and somebody ringing a
 * colleague about a frozen laptop usually does not want either camera on.
 *
 * A COMPONENT BECAUSE "EVERYWHERE" IS THE REQUIREMENT. One control, so the
 * chat composer and anything added later behave identically and the
 * query-string contract with the session page is stated in exactly one place.
 *
 * A CALL BELONGS TO A TICKET, UNLESS THE FIRM SAYS OTHERWISE. The schema's
 * rule until migration 038 was that ticket_sessions.ticket_id is NOT NULL,
 * which is what puts the screen share, the transcript and the "who looked at
 * whose machine for how long" line on the ticket. Gordon then asked, on 15
 * October, for a setting allowing calls "without a ticket being chosen
 * (General)". So with no ticket in view there are now two answers:
 *
 *   `person` and `mayCall` - the General thread with somebody, and the firm's
 *   switch read off that thread by ChatPage. On, the button works and the
 *   call is recorded in that conversation instead of on a ticket. Off, the
 *   button stays, disabled, and its title says who can change that - because
 *   a control that fails when pressed is worse than one that says why it
 *   cannot, and a control that vanishes leaves somebody looking for it.
 *
 *   `group` and `mayCall` - a call to a chat group, for everybody in it at
 *   once. The same shape as `person`: the firm has a switch for group calls,
 *   ChatPage reads it off the group, and off means disabled-with-a-reason
 *   rather than missing. The session page opens it through
 *   /chat/groups/:id/session and rings every member.
 *
 * AND THE DEVICES ARE CHOSEN HERE TOO (22 October), under the two options:
 * a "Choose devices" row that opens the DevicePanel inside the menu. The
 * choice is remembered the moment it is made, and the session page reads it
 * as it opens the microphone and camera - so there is no second step and
 * nothing to pass in the URL.
 */
// `thread` is a topic's id when the conversation open is a named one under
// General (migration 061); the call then lands in that topic.
const CallButton = ({ ticketId, person = null, group = null, thread = null, mayCall = false,
  label = 'Call', compact = false }) => {
  const [open, setOpen] = useState(false);
  // The devices section, folded by default: most calls go out on whatever
  // was chosen last time, and three selects above two buttons would bury
  // the buttons.
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [choice, setChoice] = useState(remembered);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  // How far the open menu has to slide sideways to stay on the screen.
  const [shift, setShift] = useState(0);
  const navigate = useNavigate();
  // Where the call is started from, so the session page can bring the person
  // back here when it ends. Gordon: a call started from chat goes back to
  // chat; one started from the ticket goes back to the ticket.
  const location = useLocation();

  // A menu that stays open after a click elsewhere is a menu that covers the
  // thing somebody clicked.
  useEffect(() => {
    if (!open) { setDevicesOpen(false); return undefined; }
    const away = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  /*
   * KEEP THE MENU ON THE SCREEN, wherever the button happens to be.
   *
   * Gordon, on a phone in chat: the voice-or-video menu was not fully in
   * view. The phone rule anchored the menu's LEFT edge to the button, which
   * is right for a button at the left of a narrow composer - and in chat the
   * button sits at the far right, beside Send, so the menu ran off the right
   * edge. Anchoring the other way just moves the problem to whichever page
   * puts the button on the left. No anchor is right everywhere this control
   * is used, so the menu is measured once it is drawn and slid back inside
   * the viewport, with a small margin, before the browser paints it.
   *
   * Measured again when the devices section opens: the menu grows wider for
   * the selects, and a menu that was on the screen a moment ago may not be.
   *
   * AND NOT UNDER ANYTHING. With the devices open the menu is tall, and on a
   * phone it opens upwards from the composer far enough to run under the
   * sticky bars above it - the navigation bar, and in chat the conversation's
   * own header - which then take the tap meant for "Voice call", the first
   * row. Which bars, and how tall, is not this component's to know ("ensure
   * this works everywhere"), so it asks the browser instead: elementFromPoint
   * down the menu's middle until the menu itself is what is hit, and the
   * height is capped there so the menu scrolls inside the space that is
   * really its own. A ResizeObserver repeats the measurement when the camera
   * preview appears and makes it taller again, where there is one.
   */
  const [cap, setCap] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setShift(0); setCap(null); return undefined; }
    const el = menuRef.current;
    if (!el) return undefined;
    const margin = 8;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      let dx = 0;
      if (box.right > width - margin) dx = (width - margin) - box.right;
      if (box.left + dx < margin) dx = margin - box.left;
      if (dx !== 0) setShift((s) => s + dx);
      // The LOWEST covered point, not the first clear one: a sticky header
      // sits below the navigation bar, and a menu poking out above the header
      // is clear at its very top and covered just under it.
      const x = box.left + dx + box.width / 2;
      let covered = null;
      for (let y = Math.max(0, box.top); y < box.bottom - 120; y += 6) {
        const hit = document.elementFromPoint(x, y + 2);
        if (!hit || !el.contains(hit)) covered = y + 2;
      }
      if (covered !== null) setCap(Math.max(120, Math.floor(box.bottom - covered - margin)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, devicesOpen]);

  /** A choice is kept the moment it is made; the call then opens with it. */
  const choose = (kind, device) => { setChoice(remember(kind, device)); };

  const go = (withVideo) => {
    setOpen(false);
    /*
     * ASK FOR NOTIFICATION PERMISSION HERE, which is the only moment the
     * question makes sense: somebody has just shown they intend to use
     * calling. Asking on page load gets refused, and a refusal is permanent
     * until somebody digs into browser settings - so one badly-timed prompt
     * costs the feature for good. The answer arrives too late for this call
     * and is right for every one after it. Fire and forget: nothing waits on
     * it and nothing breaks without it.
     */
    askToNotify().catch(() => {});
    // The session page reads this and turns the camera on as it joins, so the
    // choice made here is the state the call opens in.
    const q = new URLSearchParams();
    if (withVideo) q.set('video', '1');
    if (!ticketId && !onGroup && thread) q.set('thread', thread);
    q.set('from', `${location.pathname}${location.search}`);
    navigate(ticketId
      ? `/tickets/${ticketId}/session?${q}`
      : onGroup
        ? `/chat/group/${group.id}/session?${q}`
        : `/chat/${person.kind}/${person.id}/session?${q}`);
  };

  const onGroup = !ticketId && Boolean(group?.id);
  const onThread = !ticketId && !onGroup && person?.kind && person?.id;
  if (!ticketId && !((onThread || onGroup) && mayCall === true)) {
    return (
      <button
        type="button"
        className={`call-btn${compact ? ' call-btn-compact' : ''}`}
        disabled
        data-why={onGroup ? 'group-setting' : onThread ? 'setting' : 'no-ticket'}
        title={onGroup
          ? 'Group calls are switched off for the firm — ask an administrator to '
            + 'allow them in Settings'
          : onThread
            ? 'Calls without a ticket are switched off for the firm — open one of '
              + 'your shared tickets to call about, or ask an administrator to allow '
              + 'them in Settings'
            : 'Open one of your shared tickets to call about — a call is recorded on the ticket'}
      >
        {label}
      </button>
    );
  }

  // One line under the Choose-devices row saying what is chosen, so the
  // common case - it is already right - needs no opening.
  const chosenLine = KINDS.map((k) => choice[k]?.label).filter(Boolean);

  return (
    <span className="call-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`call-btn${compact ? ' call-btn-compact' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>
      {open && (
        <div className="call-menu" role="menu" ref={menuRef} data-devices={devicesOpen || undefined}
          style={{
            ...(shift ? { transform: `translateX(${shift}px)` } : {}),
            ...(cap ? { maxHeight: `${cap}px` } : {})
          }}>
          <button type="button" role="menuitem" onClick={() => go(false)}>
            <strong>Voice call</strong>
            <small>Talk, and share a screen if you need to</small>
          </button>
          <button type="button" role="menuitem" onClick={() => go(true)}>
            <strong>Video call</strong>
            <small>Your camera on from the start</small>
          </button>
          {supported() && (
            <>
              <button type="button" role="menuitem" className="call-devices-row"
                aria-expanded={devicesOpen} aria-controls="call-devices-panel"
                onClick={() => setDevicesOpen((v) => !v)}>
                <strong>{devicesOpen ? 'Hide devices' : 'Choose devices'}</strong>
                <small>
                  {chosenLine.length ? chosenLine.join(' · ')
                    : 'Microphone, speakers and camera — the system’s choice for now'}
                </small>
              </button>
              {devicesOpen && (
                <div id="call-devices-panel" className="call-menu-devices" role="group"
                  aria-label="Devices">
                  <DevicePanel choice={choice} onChoose={choose} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </span>
  );
};

export default CallButton;
